/**
 * board-math — Pure board geometry, setup-zone rules & King Shield validation.
 *
 * This module is the single source of truth on the client for:
 *   • Square <-> index <-> (file,rank) coordinate mapping.
 *   • Setup-zone boundary enforcement (White ranks 1-2, Black ranks 7-8).
 *   • King Shield proactive visual enforcement (King MUST sit on the back rank
 *     and is forbidden on the front rank of the player's setup zone).
 *   • "High-value piece on the front rank" risk detection (Q/R) — UI-only
 *     warning, the server never enforces it (matches BACKEND.md §2.2).
 *   • Pawn-promotion terminal-rank detection used by the drag-drop engine to
 *     intercept a drop and open the promotion selector.
 *
 * Per rule.md §2.3, **Castling is completely abolished** in Fog Chess: there are
 * no castling-rights tokens, no two-square King helpers, no FEN KQkq handling
 * anywhere in this module. The King moves strictly one square in any direction
 * upstream (in the gameplay renderer); board-math intentionally exposes nothing
 * that would imply castling exists.
 *
 * Everything here is pure: no DOM, no React, no socket IO. Unit-testable in
 * isolation under Vitest.
 */

import type {
  Color,
  PieceType,
  Square,
  File,
  Rank,
} from '../types/common';
import {
  FILES,
  RANKS,
  BACK_RANKS,
  oppositeColor,
  isValidSquare,
} from '../types/common';
import {
  KING_SQUARES,
  KING_SHIELD_SQUARES,
  REQUIRED_PIECE_COUNTS,
} from '../types/game';
import type { PiecePlacement } from '../types/socket';
import type { SetupValidationResult } from '../types/game';

// ---------------------------------------------------------------------------
// Coordinate geometry
// ---------------------------------------------------------------------------

/**
 * Visual rendering convention: the board is rendered top-left = a8 for a
 * White-perspective viewer (and rotated for Black). The "row/col" helpers
 * below provide a stable 0-7 grid abstraction independent of perspective,
 * where row 0 is White's back rank (rank 1 -> row 0) and row 7 is Black's
 * back rank (rank 8 -> row 7). This is the canonical mapping used by the
 * renderer and by drag-and-drop hit-testing.
 */
export interface BoardCell { row: number; col: number; square: Square; }

/** Convert a Square to its (row, col) cell on the canonical board grid. */
export function squareToCell(square: Square): BoardCell {
  const fileIdx = FILES.indexOf(square[0] as File);
  const rankIdx = RANKS.indexOf(square[1] as Rank);
  // Rank "1" lives at row 0, rank "8" at row 7.
  const row = rankIdx;
  const col = fileIdx;
  return { row, col, square };
}

/** Convert a (row, col) cell back to its Square. */
export function cellToSquare(row: number, col: number): Square {
  if (row < 0 || row > 7 || col < 0 || col > 7) {
    throw new RangeError(`cellToSquare: out of range row=${row}, col=${col}`);
  }
  const file = FILES[col] as File;
  const rank = RANKS[row] as Rank;
  return `${file}${rank}` as Square;
}

/**
 * Cheap square-color helper for CSS square rendering (light/dark).
 *
 * Canonical FIDE convention: a1 is a DARK square. The parity of
 * `fileIdx + rankIdx` is even on dark squares (e.g. a1 -> 0+0, h8 -> 7+7)
 * and odd on light squares (e.g. h1 -> 7+0, a8 -> 0+7, e4 -> 4+3).
 */
export function isLightSquare(square: Square): boolean {
  const fileIdx = FILES.indexOf(square[0] as File);
  const rankIdx = RANKS.indexOf(square[1] as Rank);
  return (fileIdx + rankIdx) % 2 === 1;
}

/** Distance between two squares in king-steps (Chebyshev). Used by UI hints
 *  but NOT for legality — move legality lives on the server. */
export function kingStepDistance(a: Square, b: Square): number {
  const ca = squareToCell(a);
  const cb = squareToCell(b);
  return Math.max(Math.abs(ca.row - cb.row), Math.abs(ca.col - cb.col));
}

// ---------------------------------------------------------------------------
// Setup-zone boundaries
// ---------------------------------------------------------------------------

/**
 * Front / back rank of a player's RTS setup zone.
 *
 * For White the zone is ranks 1-2: back rank = "1", front rank = "2" (the
 * rank closest to the contact line / the opponent).
 * For Black the zone is ranks 7-8: back rank = "8", front rank = "7".
 */
export const SETUP_BACK_RANK: Record<Color, Rank> = {
  white: '1',
  black: '8',
} as const;

export const SETUP_FRONT_RANK: Record<Color, Rank> = {
  white: '2',
  black: '7',
} as const;

/** Full ordered list of squares a player may place pieces on during setup. */
export function setupZone(color: Color): Square[] {
  const back = SETUP_BACK_RANK[color];
  const front = SETUP_FRONT_RANK[color];
  const cells: Square[] = [];
  for (const rank of [back, front] as Rank[]) {
    for (const file of FILES) {
      cells.push(`${file}${rank}` as Square);
    }
  }
  return cells;
}

/** Is the given square part of this player's setup zone? */
export function isInSetupZone(square: Square, color: Color): boolean {
  const r = square[1] as Rank;
  return BACK_RANKS[color].includes(r);
}

/** Is the given square on this player's back rank (where the King MUST sit)? */
export function isBackRank(square: Square, color: Color): boolean {
  return (square[1] as Rank) === SETUP_BACK_RANK[color];
}

/** Is the given square on this player's front rank (King-forbidden, Q/R warned)? */
export function isFrontRank(square: Square, color: Color): boolean {
  return (square[1] as Rank) === SETUP_FRONT_RANK[color];
}

// ---------------------------------------------------------------------------
// Mandate A: Proactive King Shield + setup enforcement
// ---------------------------------------------------------------------------

/**
 * Drop policy result returned by {@link canDropPiece}. The boolean `value` is
 * discriminated by `reason` so the UI can render an explanation tooltip /
 * disabled overlay type without re-deriving the rule.
 */
export type DropPolicyReason =
  | 'ok'
  | 'outside_setup_zone'
  | 'king_on_front_rank'
  | 'square_occupied'
  | 'unknown_phase';

export interface DropPolicy {
  value: boolean;
  reason: DropPolicyReason;
}

/** Phase gate — board-math is setup-phase aware by tag, not by store plumbing. */
export type BoardPhase = 'setup' | 'in_progress' | 'finished' | 'waiting';

/**
 * The heart of Mandate A: should the drag-drop engine allow `piece` to be
 * dropped on `square` for the viewer of `color`, given the current `phase`?
 *
 * Rules enforced here (purely, client-side, for UX — server still validates):
 *   • During SETUP:
 *     - All pieces restricted to the player's setup zone (ranks 1-2 / 7-8).
 *     - The King is forbidden on the front rank (Mandate A core rule); the
 *       King MUST be on the back rank. The classic "in-front-of-king shield"
 *       requirement is enforced at submit-time by {@link validateKingShield}
 *       because it needs the full placement set, not a single drop gesture.
 *     - A square already occupied by another placed piece rejects the drop.
 *   • Outside SETUP: drops are not appraised here; the drag-drop engine in
 *     gameplay delegates legality to the server (Fog of War).
 */
export function canDropPiece(
  square: Square,
  piece: PieceType,
  color: Color,
  phase: BoardPhase,
  occupied: ReadonlyArray<PiecePlacement> = [],
): DropPolicy {
  if (!isValidSquare(square)) {
    return { value: false, reason: 'outside_setup_zone' };
  }

  if (phase !== 'setup') {
    return { value: true, reason: 'ok' };
  }

  if (!isInSetupZone(square, color)) {
    return { value: false, reason: 'outside_setup_zone' };
  }

  if (piece === 'K' && isFrontRank(square, color)) {
    return { value: false, reason: 'king_on_front_rank' };
  }

  if (occupied.some((p) => p.square === square)) {
    return { value: false, reason: 'square_occupied' };
  }

  return { value: true, reason: 'ok' };
}

/**
 * High-value risk detection — purely for the live "Blind Sniping" banner.
 * Returns true when a Queen or Rook is placed on the player's FRONT rank
 * during setup. The server permits this (matches BACKEND.md §2.2 "Risk vs.
 * Reward"); board-math only flags it so the UI can warn.
 *
 * King is intentionally excluded: by {@link canDropPiece} it never lands on
 * the front rank, so it shouldn't trigger this banner. Knights/Bishops/Pawns
 * are not "high value" for sniping risk and are excluded.
 */
const HIGH_VALUE_SNIPABLE: ReadonlySet<PieceType> = new Set<PieceType>(['Q', 'R']);

export function isHighValueFrontRankDrop(
  square: Square,
  piece: PieceType,
  color: Color,
  phase: BoardPhase,
): boolean {
  if (phase !== 'setup') return false;
  if (!HIGH_VALUE_SNIPABLE.has(piece)) return false;
  return isFrontRank(square, color);
}

// ---------------------------------------------------------------------------
// Pawn promotion interception (used by the gameplay drag-drop engine)
// ---------------------------------------------------------------------------

/**
 * Terminal rank for pawn promotion, per mover color. A White pawn reaching
 * rank 8 (or a Black pawn reaching rank 1) is eligible for promotion. The
 * drag-drop engine intercepts the drop and opens the promotion selector
 * instead of emitting `move:submit` immediately.
 */
export const PROMOTION_RANK: Record<Color, Rank> = {
  white: '8',
  black: '1',
} as const;

/** Detect a drop that requires pawn-promotion interception. */
export function isPromotionDrop(
  from: Square,
  to: Square,
  piece: PieceType,
  color: Color,
): boolean {
  if (piece !== 'P') return false;
  if (!isValidSquare(from) || !isValidSquare(to)) return false;
  return (to[1] as Rank) === PROMOTION_RANK[color];
}

// ---------------------------------------------------------------------------
// Setup submission validation (mirrors backend SetupValidator per-player tier)
// ---------------------------------------------------------------------------

/**
 * Validate a candidate final placement set for the given color. Returns a
 * {@link SetupValidationResult} describing the precise errors/warnings.
 *
 * Validation tiers (mirror BACKEND.md / `setup_phase.py`):
 *   1. Inventory*: counts per {@link REQUIRED_PIECE_COUNTS} (K1 Q1 R2 B2 N2 P8).
 *   2. Zone: every placement lives inside this color's setup zone.
 *   3. Uniqueness: no two pieces share a square.
 *   4. King Shield (Mandate A core): the King is on its canonical back-rank
 *      square AND a friendly piece sits immediately in front of it on the
 *      canonical King-shield square.
 *
 * *Note: 16 pieces total (1+1+2+2+2+8 = 16) is implied by inventory parity.
 */
export function validatePlayerSetup(
  placements: ReadonlyArray<PiecePlacement>,
  color: Color,
): SetupValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Tier 1 — inventory.
  const counts: Partial<Record<PieceType, number>> = {};
  for (const p of placements) {
    counts[p.type] = (counts[p.type] ?? 0) + 1;
  }
  (Object.keys(REQUIRED_PIECE_COUNTS) as PieceType[]).forEach((type) => {
    const required = REQUIRED_PIECE_COUNTS[type];
    const actual = counts[type] ?? 0;
    if (actual !== required) {
      errors.push(
        `Invalid ${type} count: expected ${required}, got ${actual}`,
      );
    }
  });

  // Tier 2 — zone.
  for (const p of placements) {
    if (!isInSetupZone(p.square, color)) {
      errors.push(`${p.type} placed outside setup zone on ${p.square}`);
    }
  }

  // Tier 3 — uniqueness.
  const seen = new Set<Square>();
  for (const p of placements) {
    if (seen.has(p.square)) {
      errors.push(`Square ${p.square} occupied by more than one piece`);
    }
    seen.add(p.square);
  }

  // Tier 4 — King Shield. King must be on its back-rank start square AND a
  // friendly piece must sit on the canonical King-shield square in front of
  // the King. The shield can be any friendly piece (pawn/bishop/rook/etc.).
  const kingHome = KING_SQUARES[color];
  const shieldHome = KING_SHIELD_SQUARES[color];

  const king = placements.find((p) => p.type === 'K');
  if (!king) {
    errors.push(`King missing`);
  } else if (king.square !== kingHome) {
    errors.push(`King must be on ${kingHome} (found on ${king.square})`);
  }

  const shield = placements.find((p) => p.square === shieldHome);
  if (!shield) {
    errors.push(`King shield piece required on ${shieldHome}`);
  }

  // Soft warning: high-value (Q/R) on the front rank (sniping risk).
  for (const p of placements) {
    if (isHighValueFrontRankDrop(p.square, p.type, color, 'setup')) {
      warnings.push(
        `High-value ${p.type} on front rank ${p.square} exposes it to blind sniping on move 1`,
      );
    }
  }

  return {
    valid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/** Convenience boolean — is a candidate setup ready to submit? */
export function isSetupSubmittable(
  placements: ReadonlyArray<PiecePlacement>,
  color: Color,
): boolean {
  return validatePlayerSetup(placements, color).valid;
}

/** Total required pieces (sanity check for the setup sandbox tray). */
export const REQUIRED_PIECE_TOTAL = 16;

/**
 * The canonical 16-piece inventory the setup sandbox tray hands out, in tray
 * order (King first so the user can anchor the King early). This is the exact
 * set the backend's `SetupValidator` expects to see in the placements array.
 */
export const SETUP_TRAY_INVENTORY: ReadonlyArray<PieceType> = [
  'K',
  'Q',
  'R', 'R',
  'B', 'B',
  'N', 'N',
  'P', 'P', 'P', 'P', 'P', 'P', 'P', 'P',
] as const;

// ---------------------------------------------------------------------------
// Castling abolition guard (rule.md §2.3)
// ---------------------------------------------------------------------------

/**
 * There is no castling in Fog Chess. This constant exists so the gameplay
 * renderer / move-review reader can refuse to acknowledge Shredder-FEN
 * castling-rights tokens even if a `truth_fen` ever surfaces containing them,
 * and so future contributors don't accidentally wire castling back in.
 *
 * The King moves strictly one square in any direction. There are zero
 * two-square King helpers in this module by design.
 */
export const CASTLING_ENABLED: false = false as const;

/**
 * Mirror helper for callers that want to be explicit about validating a
 * candidate King move's geometry (one square, any direction) — used purely
 * for *UI hint* highlighting, never for legality (the server is authoritative
 * for in-progress legality under Fog of War).
 *
 * Returns true iff `from` and `to` are exactly one king-step apart.
 */
export function isOneKingStep(from: Square, to: Square): boolean {
  if (from === to) return false;
  if (!isValidSquare(from) || !isValidSquare(to)) return false;
  return kingStepDistance(from, to) === 1;
}

// ---------------------------------------------------------------------------
// Opponent-side helpers (Fog of War orientation)
// ---------------------------------------------------------------------------

/** The viewer's enemy color for the current match perspective. */
export function opponentColorOf(color: Color): Color {
  return oppositeColor(color);
}

/**
 * Returns the same square name from the opponent's perspective, i.e. the
 * square the *opponent* would label a piece on if we mirror the board 180°.
 * Useful for translating incoming `move:applied` / `piece:captured` payloads
 * into the viewer's coordinate frame when the viewer is Black.
 *
 * a1 <-> h8, e2 <-> e7, etc.
 */
export function mirrorSquare(square: Square): Square {
  const fileIdx = FILES.indexOf(square[0] as File);
  const rankIdx = RANKS.indexOf(square[1] as Rank);
  const mirrorFile = FILES[7 - fileIdx] as File;
  const mirrorRank = RANKS[7 - rankIdx] as Rank;
  return `${mirrorFile}${mirrorRank}` as Square;
}
